// Latency-measurement burn-in: a strip of black/white blocks along the bottom
// of the picture encoding the wall-clock time (ms, mod 2^32) at which the frame
// was processed on the server, just before encoding.
//
// The Python runner (pipelines/runner/gst_runner.py) draws it; the client decodes
// it from each displayed frame and compares against its server-synchronised clock.
// The /latency page also draws it so a camera can film a screen for true
// glass-to-glass measurement. Keep all three in sync with this layout.
//
// Layout, in units of one block `b = width / 80` (16 px at 1280 wide):
//   - background: black rectangle, x = b/2 .. b/2 + (TOTAL_BLOCKS + 1) * b, height 2b, bottom margin b/2
//   - blocks start at x0 = b, y0 = height - 2b (square b x b)
//   - [WHITE, BLACK] start marker, 32 data bits MSB first, 8-bit checksum
//   - white = 1, black = 0. Checksum = sum of the 4 data bytes, mod 256.

export const BURNIN_DATA_BITS = 32;
export const BURNIN_CHECK_BITS = 8;
export const BURNIN_TOTAL_BLOCKS = 2 + BURNIN_DATA_BITS + BURNIN_CHECK_BITS;

export interface BurninGeometry {
  block: number;
  x0: number;
  y0: number;
}

export function burninGeometry(width: number, height: number): BurninGeometry {
  const block = width / 80;
  return { block, x0: block, y0: height - 2 * block };
}

export function checksum32(value: number): number {
  const v = value >>> 0;
  return ((v >>> 24) + ((v >>> 16) & 0xff) + ((v >>> 8) & 0xff) + (v & 0xff)) & 0xff;
}

/** Bits to draw for a timestamp, including the start marker. */
export function encodeBurnin(epochMs: number): number[] {
  const v = Number(BigInt(Math.floor(epochMs)) & 0xffffffffn);
  const bits = [1, 0];
  for (let i = BURNIN_DATA_BITS - 1; i >= 0; i--) bits.push((v >>> i) & 1);
  const c = checksum32(v);
  for (let i = BURNIN_CHECK_BITS - 1; i >= 0; i--) bits.push((c >>> i) & 1);
  return bits;
}

/**
 * Decode from luma samples taken at the centre of each block.
 * Returns the 32-bit timestamp, or null when the marker/checksum don't match
 * (no burn-in present, or a frame mid-transition).
 */
export function decodeBurnin(luma: number[]): number | null {
  if (luma.length !== BURNIN_TOTAL_BLOCKS) return null;
  // Threshold halfway between the marker's white and black blocks: robust to
  // limited-range video and display gamma.
  const white = luma[0];
  const black = luma[1];
  if (white - black < 60) return null;
  const mid = (white + black) / 2;
  const bits = luma.map((l) => (l > mid ? 1 : 0));
  let v = 0;
  for (let i = 0; i < BURNIN_DATA_BITS; i++) v = ((v << 1) | bits[2 + i]) >>> 0;
  let c = 0;
  for (let i = 0; i < BURNIN_CHECK_BITS; i++) c = (c << 1) | bits[2 + BURNIN_DATA_BITS + i];
  return checksum32(v) === c ? v : null;
}

/**
 * Turn a decoded 32-bit timestamp back into full epoch ms using a reference
 * clock that is known to be within ~24 days of it.
 */
export function unwrapBurnin(value32: number, referenceEpochMs: number): number {
  const span = 2 ** 32;
  const base = referenceEpochMs - (referenceEpochMs % span);
  let t = base + value32;
  if (t - referenceEpochMs > span / 2) t -= span;
  else if (referenceEpochMs - t > span / 2) t += span;
  return t;
}
